import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FiSave, FiArrowLeft } from 'react-icons/fi';
import helperService from '../services/helperService';
import technicianService from '../services/technicianService';
import Input from '../components/Input';
import Button from '../components/Button';
import './TechnicianForm.css';

const HelperForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [technicians, setTechnicians] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    employeeId: '',
    assignedTo: '',
    salary: '',
    status: 'available',
  });

  useEffect(() => {
    fetchTechnicians();
    if (isEdit) {
      fetchHelper();
    }
  }, [id]);

  const fetchTechnicians = async () => {
    try {
      const response = await technicianService.getAll();
      setTechnicians(response.data);
    } catch (error) {
      console.error('Failed to fetch technicians');
    }
  };

  const fetchHelper = async () => {
    try {
      const response = await helperService.getById(id);
      const helper = response.data;
      setFormData({
        name: helper.name,
        phone: helper.phone,
        employeeId: helper.employeeId,
        assignedTo: helper.assignedTo?._id || '',
        salary: helper.salary || '',
        status: helper.status,
      });
    } catch (error) {
      toast.error('Failed to fetch helper details');
      navigate('/helpers');
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const submitData = {
      ...formData,
      assignedTo: formData.assignedTo || undefined,
      salary: Number(formData.salary),
    };

    try {
      if (isEdit) {
        await helperService.update(id, submitData);
        toast.success('Helper updated successfully');
      } else {
        await helperService.create(submitData);
        toast.success('Helper added successfully');
      }
      navigate('/helpers');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Operation failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="form-container">
      <div className="form-header">
        <button onClick={() => navigate('/helpers')} className="back-button">
          <FiArrowLeft /> Back
        </button>
        <h2 className="form-title">
          {isEdit ? 'Edit Helper' : 'Add New Helper'}
        </h2>
      </div>

      <form onSubmit={handleSubmit} className="form-card">
        <div className="form-section">
          <h3 className="section-title">Basic Information</h3>
          <div className="form-grid">
            <Input
              label="Full Name"
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="Helper Name"
              required
            />

            <Input
              label="Employee ID"
              type="text"
              name="employeeId"
              value={formData.employeeId}
              onChange={handleChange}
              placeholder="HELP001"
              required
              disabled={isEdit}
            />

            <Input
              label="Phone Number"
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              placeholder="9876543210"
              required
            />

            <Input
              label="Monthly Salary (₹)"
              type="number"
              name="salary"
              value={formData.salary}
              onChange={handleChange}
              placeholder="15000"
              min="0"
            />
          </div>
        </div>

        <div className="form-section">
          <h3 className="section-title">Assignment & Status</h3>
          <div className="form-grid">
            <div className="input-group">
              <label className="input-label">Assign to Technician</label>
              <select
                name="assignedTo"
                value={formData.assignedTo}
                onChange={handleChange}
                className="input-field"
              >
                <option value="">Not Assigned</option>
                {technicians.map((tech) => (
                  <option key={tech._id} value={tech._id}>
                    {tech.name} ({tech.employeeId})
                  </option>
                ))}
              </select>
            </div>

            <div className="input-group">
              <label className="input-label">Status</label>
              <select
                name="status"
                value={formData.status}
                onChange={handleChange}
                className="input-field"
              >
                <option value="available">Available</option>
                <option value="on_site">On Site</option>
                <option value="off_duty">Off Duty</option>
                <option value="on_leave">On Leave</option>
              </select>
            </div>
          </div>
        </div>

        <div className="form-actions">
          <Button
            type="button"
            variant="secondary"
            onClick={() => navigate('/helpers')}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            icon={<FiSave />}
            loading={loading}
          >
            {isEdit ? 'Update Helper' : 'Add Helper'}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default HelperForm;
